import bbox from '@turf/bbox';
import { useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import { get } from '@alltrails/core/utils/get';
import {
  ExploreServiceState,
  updateDistanceAway,
  updateDistanceAwayIsochrone,
  updateDistanceAwayLoading,
  updateExploreActionType
} from '../redux/exploreServiceSlice';
import DistanceAway from '../types/DistanceAway';
import ExploreEventData from '../types/ExploreEventData';
import IsochroneResponse from '../types/IsochroneResponse';

const areDistanceAwaysEqual = (da1?: DistanceAway, da2?: DistanceAway) => {
  if (da1 && da2) {
    return da1.distance === da2.distance && da1.center.lat === da2.center.lat && da1.center.lng === da2.center.lng;
  }
  return da1 === da2;
};

const useDistanceAway = (map?: MapRef, onError?: () => void) => {
  const dispatch = useDispatch();
  const { distanceAway, distanceAwayIsochrone } = useSelector((state: { exploreService: ExploreServiceState }) => state.exploreService);

  // Just used to compare the new distance away to the old one to determine if a filter was applied or cleared.
  // If no isochrone is applied on initial call, act like no distance away is set so that we can fetch an isochrone
  // on the initial load.
  const previousDistanceAway = useRef(distanceAwayIsochrone ? distanceAway : undefined);

  useEffect(() => {
    if (areDistanceAwaysEqual(distanceAway, previousDistanceAway.current)) {
      return;
    }
    previousDistanceAway.current = distanceAway;

    if (distanceAway) {
      dispatch(updateDistanceAwayLoading(true));
      dispatch(updateDistanceAway(distanceAway));
      const { lat, lng } = distanceAway.center;
      // meters must be a whole number
      get<IsochroneResponse>('/api/alltrails/isochrone', { params: { lat, lng, meters: Math.round(distanceAway.distance) } })
        .then(data => {
          if (data) {
            const isochrone = data.message.features[0].geometry;
            dispatch(updateDistanceAwayIsochrone(isochrone));
            dispatch(updateExploreActionType('filter applied'));
            if (map) {
              const isochroneBoundingBox = bbox(isochrone) as [number, number, number, number];
              const padding = map.getPadding();
              const eventData: ExploreEventData = { preserveLocationResult: true };
              map.fitBounds(isochroneBoundingBox, { padding }, eventData);
            }
          }
        })
        .catch(() => {
          onError?.();
          dispatch(updateDistanceAway(undefined));
          dispatch(updateDistanceAwayIsochrone(undefined));
        })
        .finally(() => {
          dispatch(updateDistanceAwayLoading(false));
        });
    } else {
      dispatch(updateDistanceAway(undefined));
      dispatch(updateDistanceAwayIsochrone(undefined));
      dispatch(updateExploreActionType('clear filter'));
    }
  }, [dispatch, distanceAway, map, onError]);
};

export default useDistanceAway;
